import React, { useCallback, useContext, useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { ThemeContext } from 'styled-components'
import { debounce } from 'debounce'
import Checkbox from '@8x8/oxygen-checkbox'
import { CheckIcon } from '@8x8/oxygen-icon'
import Tooltip from '@8x8/oxygen-tooltip'

import { getSecuritySettings, isLargePreset, getOverridePermissions } from '~/selectors/room-settings'
import { updateSecuritySettings } from '~/actions/room-settings'
import Input from '~/components/common/input'
import { translate } from '~/core/translate'
import { PASSCODE_VALIDATION } from '~/core/constants'
import { SettingsContainer, SettingsDescription, SettingsTitle, StyledLegend } from './components'

const PasscodeSection = styled.div`
  margin-left: 4px; 
`

const StyledSettingsDescription = styled(SettingsDescription)`
  margin: 0 0 16px 36px;
`

const StyledPasscodeDescription = styled(SettingsDescription)`
margin: 0 0 8px 0;
`

const IndentedContainer = styled.div`
  margin-left: 32px;
  margin-top: 8px;
`

const SecuritySettings = () => {
  const dispatch = useDispatch()
  const theme = useContext(ThemeContext)
  const {
    passcode,
    goLiveEnabled,
    waitForApproval,
    waitForModerator
  } = useSelector(getSecuritySettings)
  const [statePasscode, setStatePasscode] = useState(passcode ?? '')
  const isLargeMeeting = useSelector(isLargePreset)
  const overridePermissions = useSelector(getOverridePermissions)

  const isLobbyDisabled = useMemo(
    () => !overridePermissions.lobbyEnabled,
    [overridePermissions.lobbyEnabled]
  )

  useEffect(() => {
    setStatePasscode(passcode ?? '')
  }, [passcode])

  const handleUpdate = useCallback(payload => {
    dispatch(updateSecuritySettings(payload))
  }, [])

  const debouncedUpdate = useCallback(debounce(
    payload => handleUpdate(payload), 2500
  ), [])

  const handleDebouncedUpdate = useCallback(payload => {
    debouncedUpdate(payload)
  }, [debouncedUpdate])

  const handlePasscodeChange = useCallback(e => {
    const updatedPasscode = e.target.value

    if (!updatedPasscode.length) {
      setStatePasscode('')
      return
    }

    if (!PASSCODE_VALIDATION.test(updatedPasscode)) {
      return
    }

    setStatePasscode(updatedPasscode)
    handleDebouncedUpdate({ passcode: updatedPasscode })
  }, [])

  const renderPasscodeInputIcon = useCallback(() => {
    const isValidPasscode = passcode !== undefined &&
      statePasscode !== '' &&
      passcode === statePasscode

    return isValidPasscode ? <CheckIcon color={theme._neo.success01} /> : null
  }, [statePasscode, passcode])

  const renderPasscode = () => (
    <PasscodeSection>
      <StyledLegend>{translate('add_passcode')}</StyledLegend>
      <StyledPasscodeDescription>{translate('password_indication')}</StyledPasscodeDescription>
      <Input
        style={{ width: 'auto' }}
        placeholder={translate('passcode')}
        value={statePasscode}
        onBlur={() => {
          debouncedUpdate.clear()
          passcode !== statePasscode && handleUpdate({ passcode: statePasscode })
        }}
        onChange={handlePasscodeChange}
        onKeyPress={e => {
          if (e.key === 'Enter') {
            debouncedUpdate.clear()
            handleUpdate({ passcode: statePasscode })
          }
        }}
        maxLength={8}
        iconRight={renderPasscodeInputIcon}
      />
    </PasscodeSection>
  )

  const renderLargeMeetingSettings = () => (
    <>
      <Checkbox
        label={translate('enable_go_live')}
        isChecked={goLiveEnabled}
        onChange={() => handleUpdate({ goLiveEnabled: !goLiveEnabled })}
      />
      <StyledSettingsDescription>{translate('enable_go_live_description')}</StyledSettingsDescription>
      {renderPasscode()}
    </>
  )

  const renderStandardMeetingSettings = () => {
    const moderatorCheckbox = (
      <Checkbox
        label={translate('moderator_required')}
        isChecked={waitForModerator || waitForApproval}
        isDisabled={isLobbyDisabled}
        onChange={() => {
          const newWaitForModerator = !(waitForModerator || waitForApproval)
          if (newWaitForModerator) {
            handleUpdate({ waitForModerator: true })
          } else {
            handleUpdate({ waitForModerator: false, waitForApproval: false, passcode: '' })
            setStatePasscode('')
          }
        }}
      />
    )

    const lobbyCheckbox = (
      <Checkbox
        label={translate('enable_lobby')}
        isChecked={waitForApproval}
        isDisabled={isLobbyDisabled}
        onChange={() => {
          const newWaitForApproval = !waitForApproval
          if (newWaitForApproval) {
            handleUpdate({ waitForApproval: true })
          } else {
            handleUpdate({ waitForApproval: false, waitForModerator: true, passcode: '' })
            setStatePasscode('')
          }
        }}
      />
    )

    return (
      <>
        {isLobbyDisabled
          ? (
            <Tooltip
              title={translate('admin_controlled_setting')}
              orientation='top'
              showOn='hover'
            >
              {moderatorCheckbox}
            </Tooltip>
            )
          : moderatorCheckbox}
        {(waitForModerator || waitForApproval || passcode) && (
          <IndentedContainer>
            {(waitForModerator || waitForApproval) && (
              <>
                {isLobbyDisabled
                  ? (
                    <Tooltip
                      title={translate('admin_controlled_setting')}
                      orientation='top'
                      showOn='hover'
                    >
                      {lobbyCheckbox}
                    </Tooltip>
                    )
                  : lobbyCheckbox}
              </>
            )}
            {(waitForApproval || passcode) && (
              <IndentedContainer>
                {renderPasscode()}
              </IndentedContainer>
            )}
          </IndentedContainer>
        )}
      </>
    )
  }

  return (
    <SettingsContainer>
      <SettingsTitle>{translate(isLargeMeeting ? 'other_settings' : 'security')}</SettingsTitle>
      {isLargeMeeting ? renderLargeMeetingSettings() : renderStandardMeetingSettings()}
    </SettingsContainer>
  )
}

export default SecuritySettings
